import { ChangeEvent, ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { Formik } from 'formik'
import { keys, isEmpty } from 'lodash'

import { Tag } from 'components/v2/Tag'
import { getQueryString } from 'utils/route'
import { Input } from 'components/Input'
import { InputTags } from 'components/v2/InputTags'
import { Label } from 'components/v2/Label'
import { Select } from 'components/v2/Select'
import { SelectTags } from 'components/v2/SelectTags'
import { GTAncillaryStatus } from 'gql/types.generated'
import { DateRangeFilter } from 'features/App/components/Filters/DateRangeFilter'
import { ticketInsuranceStatusMessages } from 'features/Bookings/components/TicketInsuranceStatus/messages'
import { CONCILIATION_STATUS } from 'features/Inventories/components/ConciliationStatus/constants'
import { conciliationStatusMessages } from 'features/Inventories/components/ConciliationStatus/messages'
import { dateRangeToString, getDateRangeQuery } from 'utils/date'
import { ITicketsInsuranceFilters, ITicketsInsuranceFiltersProps } from './types'
import { IDateRangeFilter } from 'features/App/components/Filters/DateRangeFilter/types'
import {
  FilterHeader,
  FilterToggle,
  FilterWrapper,
  FiltersContainer,
  FiltersSubmit,
  FiltersWrapper,
  TagsWrapper
} from 'components/Filters'

const TICKET_INSURANCE_STATUS_LIST = [
  GTAncillaryStatus.CancellationFailed,
  GTAncillaryStatus.Cancelled,
  GTAncillaryStatus.Confirmed,
  GTAncillaryStatus.CreationFailed,
  GTAncillaryStatus.EditFailed,
  GTAncillaryStatus.Expired,
  GTAncillaryStatus.Failed,
  GTAncillaryStatus.Reserved
]

export const HAS_CONFLICT_LIST = [CONCILIATION_STATUS.CONFLICTING, CONCILIATION_STATUS.COMPLETED]

const messages = defineMessages({
  reservationPlaceholder: {
    id: 'ticketsInsurance.filters.reservationPlaceholder',
    defaultMessage: 'Reservation No.'
  },
  selectStatus: {
    id: 'ticketsInsurance.filters.selectStatus',
    defaultMessage: 'Select status'
  },
  selectTravelDateRange: {
    id: 'ticketsInsurance.filters.selectTravelDateRange',
    defaultMessage: 'Select travel date range'
  },
  hasConflict: {
    id: 'ticketsInsurance.filters.selectHasConflict',
    defaultMessage: 'Select conflict status'
  },
  certificateIDsPlaceholder: {
    id: 'ticketsInsurance.filters.certificateIDsPlaceholder',
    defaultMessage: 'Certificate'
  }
})

export const TicketsInsuranceFilters = ({ filters }: ITicketsInsuranceFiltersProps): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const getFilteredStatus = (values: GTAncillaryStatus[]): GTAncillaryStatus[] => {
    return TICKET_INSURANCE_STATUS_LIST.filter((status): TSFixMe => {
      return values.indexOf(status) === -1
    }).sort((a, b): TSFixMe => {
      const aTranslation = intl.formatMessage(ticketInsuranceStatusMessages[a])
      const bTranslation = intl.formatMessage(ticketInsuranceStatusMessages[b])
      return aTranslation.localeCompare(bTranslation)
    })
  }

  const filterTicketsInsurance = (values: ITicketsInsuranceFilters): void => {
    const travelDateRangeQuery = getDateRangeQuery(values.travelDateRange)

    const query = {
      reservation: values.reservation,
      status: values.status.join(','),
      travelDateFrom: travelDateRangeQuery.from,
      travelDateTo: travelDateRangeQuery.to,
      travelDateRangeValue: travelDateRangeQuery.rangeValue,
      hasConflict: values.hasConflict,
      certificateIDs: values.certificateIDs.join(',')
    }

    navigate(`/ancillaries/insurance?${getQueryString(query)}`)
  }

  const initialValues = {
    reservation: filters.reservation || '',
    status: filters.status || [],
    travelDateRange: filters.travelDateRange || {
      from: '',
      to: '',
      rangeValue: ''
    },
    hasConflict: filters.hasConflict || '',
    certificateIDs: filters.certificateIDs || []
  }

  return (
    <Formik initialValues={initialValues} onSubmit={filterTicketsInsurance} enableReinitialize>
      {(props): ReactElement => {
        const { handleChange, handleSubmit, setFieldValue, values } = props

        const handleStatusChange = (status: GTAncillaryStatus[]): void => {
          setFieldValue('status', status)
          filterTicketsInsurance({
            ...values,
            status
          })
        }

        const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>): void => {
          const {
            target: { id, value }
          } = event

          handleChange(event)

          filterTicketsInsurance({
            ...values,
            [id]: value
          })
        }

        const handleCertificateChange = (certificateIDs: string[]): void => {
          setFieldValue('certificateIDs', certificateIDs)

          filterTicketsInsurance({
            ...values,
            certificateIDs
          })
        }

        const handleFilterDelete = (id: string): void => {
          if (id === 'status' || id === 'certificateIDs') {
            setFieldValue(id, [])
            filterTicketsInsurance({
              ...values,
              [id]: []
            })
          } else {
            setFieldValue(id, '')
            filterTicketsInsurance({
              ...values,
              [id]: ''
            })
          }
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <FilterToggle onClick={toggleFilters} />
                  <TagsWrapper>
                    {keys(filters).map((filterKey): TSFixMe => {
                      let label, value
                      const filterValue = filters[filterKey as keyof typeof filters]
                      const hasValue = !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'reservation':
                          label = <FormattedMessage id="dictionary.reservation" defaultMessage="Reservation" />
                          value = filterValue
                          break
                        case 'status':
                          label = <FormattedMessage id="dictionary.status" defaultMessage="Status" />
                          value = (filterValue as GTAncillaryStatus[])
                            .map((status): TSFixMe => {
                              return (
                                ticketInsuranceStatusMessages[status] &&
                                intl.formatMessage(ticketInsuranceStatusMessages[status])
                              )
                            })
                            .join(', ')
                          break
                        case 'travelDateRange':
                          label = <FormattedMessage id="dictionary.travelDate" defaultMessage="Travel date" />
                          value = dateRangeToString(intl, filterValue as IDateRangeFilter)

                          if (!value) return null

                          break
                        case 'hasConflict':
                          label = <FormattedMessage id="dictionary.hasConflict" defaultMessage="Conflict Status" />
                          value =
                            conciliationStatusMessages[filterValue as string] &&
                            intl.formatMessage(conciliationStatusMessages[filterValue as string])
                          break
                        case 'certificateIDs':
                          label = <FormattedMessage id="dictionary.certificateIDs" defaultMessage="Certificate" />
                          value = (filterValue as string[]).join(', ')
                          break
                      }

                      return (
                        <Tag key={filterKey} onDelete={handleFilterDelete.bind(this, filterKey)}>
                          {label}: {value as string}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <Input
                    id="reservation"
                    value={values.reservation}
                    onChange={handleChange}
                    placeholder={intl.formatMessage(messages.reservationPlaceholder)}
                    label={<FormattedMessage id="dictionary.reservation" defaultMessage="Reservation" />}
                  />
                  <SelectTags
                    id="status"
                    placeholder={intl.formatMessage(messages.selectStatus)}
                    values={values.status}
                    items={getFilteredStatus(values.status)}
                    getTagLabel={(status: GTAncillaryStatus): string => {
                      return intl.formatMessage(ticketInsuranceStatusMessages[status])
                    }}
                    onChange={handleStatusChange}
                    label={<FormattedMessage id="dictionary.status" defaultMessage="Status" />}
                  />
                  <div>
                    <Label>
                      <FormattedMessage id="dictionary.travelDateRange" defaultMessage="Travel date range" />
                    </Label>
                    <DateRangeFilter
                      value={values.travelDateRange}
                      placeholder={intl.formatMessage(messages.selectTravelDateRange)}
                      onChange={(value: IDateRangeFilter): void => {
                        setFieldValue('travelDateRange', value)

                        filterTicketsInsurance({
                          ...values,
                          travelDateRange: value
                        })
                      }}
                    />
                  </div>
                  <Select
                    id="hasConflict"
                    value={values.hasConflict}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="dictionary.hasConflict" defaultMessage="Conflict Status" />}
                  >
                    <option value="">{intl.formatMessage(messages.hasConflict)}</option>
                    {HAS_CONFLICT_LIST.map((hasConflict): TSFixMe => {
                      return (
                        <option value={hasConflict} key={hasConflict}>
                          {intl.formatMessage(conciliationStatusMessages[hasConflict])}
                        </option>
                      )
                    })}
                  </Select>
                  <InputTags
                    id="certificateIDs"
                    values={values.certificateIDs}
                    onChange={handleCertificateChange}
                    placeholder={intl.formatMessage(messages.certificateIDsPlaceholder)}
                    label={<FormattedMessage id="dictionary.certificateIDs" defaultMessage="Certificate" />}
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
